import { useCallback, useRef, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import {
  fetchCreditAccount,
  grantCredits,
  setCreditUnlimited,
  upsertCreditPolicy,
  type CreditAccount,
  type CreditPolicyMode,
} from '../../api/creditAdminApi';
import { searchUsers, type UserBrief } from '../../api/userSearchApi';

const { Title, Text } = Typography;

const BIZ_OPTIONS = [
  { value: 'cengliu', label: 'cengliu' },
  { value: 'qingfan', label: 'qingfan' },
  { value: 'aisolve', label: 'aisolve' },
];

const POLICY_MODE_OPTIONS: { value: CreditPolicyMode; label: string }[] = [
  { value: 'charge', label: '计费（charge）' },
  { value: 'unlimited', label: '无限（unlimited）' },
];

function displayName(user: UserBrief | undefined, userId: string): string {
  if (!user) return userId;
  return user.displayName || user.name || user.username || userId;
}

function formatBalance(value: number): string {
  return value.toLocaleString('zh-CN');
}

export function CreditPage() {
  const [userId, setUserId] = useState<string | undefined>();
  const [userOptions, setUserOptions] = useState<UserBrief[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserBrief | undefined>();
  const [userSearching, setUserSearching] = useState(false);

  const [account, setAccount] = useState<CreditAccount | null>(null);
  const [loading, setLoading] = useState(false);
  const [unlimitedSaving, setUnlimitedSaving] = useState(false);

  const [delta, setDelta] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [granting, setGranting] = useState(false);
  const idempotencyKeyRef = useRef(uuidv4());

  const [policyBiz, setPolicyBiz] = useState<string | undefined>();
  const [policyMode, setPolicyMode] = useState<CreditPolicyMode>('charge');
  const [policySaving, setPolicySaving] = useState(false);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadAccount = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const next = await fetchCreditAccount(id);
      setAccount(next);
    } catch (err) {
      setAccount(null);
      message.error(err instanceof Error ? err.message : '加载积分账户失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const onSearchUsers = (keyword: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = keyword.trim();
    if (!q) {
      setUserOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setUserSearching(true);
      try {
        const users = await searchUsers(q);
        setUserOptions(users);
      } catch (err) {
        message.error(err instanceof Error ? err.message : '搜索用户失败');
      } finally {
        setUserSearching(false);
      }
    }, 300);
  };

  const onSelectUser = (id: string | undefined) => {
    setUserId(id);
    setAccount(null);
    const found = userOptions.find((u) => u.id === id);
    setSelectedUser(found);
    if (id) void loadAccount(id);
  };

  const onToggleUnlimited = async (unlimited: boolean) => {
    if (!userId || !account) return;
    setUnlimitedSaving(true);
    try {
      const next = await setCreditUnlimited(userId, unlimited);
      setAccount(next);
      message.success(unlimited ? '已开启无限额度' : '已关闭无限额度');
    } catch (err) {
      message.error(err instanceof Error ? err.message : '更新无限额度失败');
    } finally {
      setUnlimitedSaving(false);
    }
  };

  const onGrant = async () => {
    if (!userId) {
      message.warning('请先选择用户');
      return;
    }
    if (delta == null || !Number.isFinite(delta) || delta === 0) {
      message.warning('请输入非零整数额度');
      return;
    }
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      message.warning('请填写调账原因');
      return;
    }
    setGranting(true);
    try {
      const next = await grantCredits(userId, {
        delta,
        reason: trimmedReason,
        idempotencyKey: idempotencyKeyRef.current,
      });
      setAccount(next);
      idempotencyKeyRef.current = uuidv4();
      setDelta(null);
      setReason('');
      message.success(`调账成功，当前余额 ${formatBalance(next.balance)}`);
    } catch (err) {
      message.error(err instanceof Error ? err.message : '调账失败');
    } finally {
      setGranting(false);
    }
  };

  const onSavePolicy = async () => {
    if (!policyBiz) {
      message.warning('请选择 bizCode');
      return;
    }
    setPolicySaving(true);
    try {
      const next = await upsertCreditPolicy(policyBiz, policyMode);
      message.success(`已将 ${next.bizCode} 策略设为 ${next.mode}`);
    } catch (err) {
      message.error(err instanceof Error ? err.message : '更新业务策略失败');
    } finally {
      setPolicySaving(false);
    }
  };

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>
        积分
      </Title>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          showSearch
          filterOption={false}
          placeholder="搜索用户（邮箱/用户名）"
          style={{ width: 320 }}
          loading={userSearching}
          value={userId}
          options={userOptions.map((u) => ({
            value: u.id,
            label: `${displayName(u, u.id)} (${u.email || u.username})`,
          }))}
          onSearch={onSearchUsers}
          onChange={onSelectUser}
        />
        <Button
          icon={<ReloadOutlined />}
          disabled={!userId}
          loading={loading}
          onClick={() => {
            if (userId) void loadAccount(userId);
          }}
        >
          刷新
        </Button>
      </Space>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title="账户" loading={loading && !account}>
          {!userId ? (
            <Text type="secondary">请搜索并选择用户后查看余额。</Text>
          ) : account ? (
            <Descriptions column={1} size="small">
              <Descriptions.Item label="用户">
                {displayName(selectedUser, userId)}
              </Descriptions.Item>
              {selectedUser?.email ? (
                <Descriptions.Item label="邮箱">{selectedUser.email}</Descriptions.Item>
              ) : null}
              <Descriptions.Item label="userId">
                <Text copyable={{ text: userId }}>{userId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="余额">
                <Text strong>{formatBalance(account.balance)}</Text>
                {account.balance < 0 ? (
                  <Tag style={{ marginLeft: 8 }}>已扣穿</Tag>
                ) : null}
              </Descriptions.Item>
              <Descriptions.Item label="无限额度">
                <Switch
                  checked={account.unlimited}
                  loading={unlimitedSaving}
                  onChange={(checked) => void onToggleUnlimited(checked)}
                />
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {account.unlimited ? '开跑不检查余额' : '按余额计费'}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Text type="secondary">未能加载该用户的积分账户。</Text>
          )}
        </Card>

        <Card title="调账">
          <Space wrap align="start">
            <InputNumber
              placeholder="额度变动"
              style={{ width: 160 }}
              precision={0}
              value={delta}
              onChange={(v) => setDelta(typeof v === 'number' ? v : null)}
            />
            <Input
              placeholder="原因（必填）"
              style={{ width: 280 }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button type="primary" disabled={!userId} loading={granting} onClick={() => void onGrant()}>
              发放 / 扣减
            </Button>
          </Space>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">正数发放、负数扣减。同一失败请求会复用幂等键，成功后自动换新。</Text>
          </div>
        </Card>

        <Card title="业务策略">
          <Space wrap align="start">
            <Select
              allowClear
              placeholder="bizCode"
              style={{ width: 180 }}
              options={BIZ_OPTIONS}
              value={policyBiz}
              onChange={setPolicyBiz}
            />
            <Select
              style={{ width: 200 }}
              options={POLICY_MODE_OPTIONS}
              value={policyMode}
              onChange={setPolicyMode}
            />
            <Button type="primary" loading={policySaving} onClick={() => void onSavePolicy()}>
              保存策略
            </Button>
          </Space>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              未登记的 biz 默认按 charge 计费。此处为覆盖写入，当前策略无法从管理接口回读。
            </Text>
          </div>
        </Card>
      </Space>
    </div>
  );
}
